import { useLanguage } from "@web/lib/contexts/language-context";
import { flashVariants } from "@web/styles/motion-variants";
import { AnimatePresence, motion } from "framer-motion";
import type { RefObject } from "react";
import { isBrowser, isMobile } from "react-device-detect";
import Webcam from "react-webcam";
import type { SealCaptureGuidance } from "../hooks/use-seal-capture-guidance";
import { CameraInitializingOverlay } from "./camera-initializing-overlay";
import { CameraStreamErrorOverlay } from "./camera-stream-error-overlay";

type CameraCaptureViewProps = {
	webcamRef: RefObject<Webcam | null>;
	facingMode: "user" | "environment";
	isCameraReady: boolean;
	cameraError: string | null;
	cameraTimeoutError: boolean;
	onCameraReady: () => void;
	onCameraError: (err: string | DOMException) => void;
	onRetryCamera: () => void;
	onCapture: () => void;
	showFlash: boolean;
	guidance: SealCaptureGuidance;
	countdown: number | null;
};

export const CameraCaptureView = ({
	webcamRef,
	facingMode,
	isCameraReady,
	cameraError,
	cameraTimeoutError,
	onCameraReady,
	onCameraError,
	onRetryCamera,
	onCapture,
	showFlash,
	guidance,
	countdown,
}: CameraCaptureViewProps) => {
	const { t } = useLanguage();
	const isDesktop = isBrowser && !isMobile;
	const hasStreamError = !!cameraError || cameraTimeoutError;
	const guidanceMessage = {
		idle: t.camera.guidancePreparing,
		find: t.camera.guidanceFind,
		detected: t.camera.guidanceDetected,
		closer: t.camera.guidanceCloser,
		farther: t.camera.guidanceFarther,
		center: t.camera.guidanceCenter,
		hold: t.camera.guidanceHold,
		capturing: t.camera.guidanceCapturing,
	}[guidance];

	return (
		<>
			<div className="relative flex flex-1 items-center justify-center bg-black">
				{!hasStreamError && (
					<Webcam
						audio={false}
						ref={webcamRef}
						screenshotFormat="image/jpeg"
						screenshotQuality={1}
						videoConstraints={{
							frameRate: { ideal: 30, max: 30 },
							facingMode,
							width: { ideal: 1920 },
							height: { ideal: 1080 },
						}}
						className="h-full w-full"
						onUserMedia={onCameraReady}
						onUserMediaError={onCameraError}
					/>
				)}

				{isDesktop && isCameraReady && !hasStreamError && (
					<button
						type="button"
						aria-label={t.camera.tapToTakePhoto}
						onClick={onCapture}
						className="absolute inset-0 z-10 cursor-pointer touch-manipulation bg-transparent focus:outline-none"
					/>
				)}

				<CameraStreamErrorOverlay
					visible={hasStreamError}
					message={cameraError || t.camera.loadingTimeout}
					onRetry={onRetryCamera}
				/>

				<CameraInitializingOverlay
					visible={!isCameraReady && !hasStreamError}
				/>

				<AnimatePresence>
					{showFlash && (
						<motion.div
							className="absolute inset-0 z-30 bg-white"
							variants={flashVariants}
							initial="hidden"
							animate="visible"
							exit="exit"
						/>
					)}
				</AnimatePresence>
			</div>

			{isCameraReady && !hasStreamError && (
				<div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 px-4 pb-[max(2rem,env(safe-area-inset-bottom))] sm:px-6">
					<div
						aria-live="polite"
						aria-atomic="true"
						className="mx-auto flex max-w-md flex-col items-center gap-4 rounded-3xl border border-white/20 bg-black/80 px-6 py-6 text-center text-white shadow-lg backdrop-blur-md"
					>
						{countdown !== null && (
							<span className="flex size-20 items-center justify-center rounded-full border-4 border-emerald-400 bg-emerald-400/10 text-4xl font-bold tabular-nums">
								{countdown}
							</span>
						)}
						<p className="text-xl leading-tight font-semibold sm:text-2xl">
							{guidanceMessage}
						</p>
						{isDesktop && countdown === null && guidance !== "capturing" && (
							<p className="text-sm text-white/80">{t.camera.tapToTakePhoto}</p>
						)}
					</div>
				</div>
			)}
		</>
	);
};
